import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AdminUser } from '../models/auth.model';
import { ReadinessStore } from '../stores/readiness.store';
import { FeatureFlagStore } from '../stores/feature-flag.store';
import { InvoicingSettingsStore } from '../stores/invoicing-settings.store';
import { PartnerTermsStore } from '../stores/partner-terms.store';
import { ComplexStore } from '../stores/complex.store';
import { VenueAccessStore } from '../stores/venue-access.store';
import { AuthService } from './auth.service';

@Injectable({ providedIn: 'root' })
export class AuthStore {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly featureFlagStore = inject(FeatureFlagStore);
  private readonly readinessStore = inject(ReadinessStore);
  private readonly partnerTermsStore = inject(PartnerTermsStore);
  private readonly complexStore = inject(ComplexStore);
  private readonly invoicingSettingsStore = inject(InvoicingSettingsStore);
  private readonly venueAccessStore = inject(VenueAccessStore);

  readonly currentAdmin = signal<AdminUser | null>(null);
  readonly isAuthenticated = computed(() => this.currentAdmin() !== null);
  readonly hasComplex = computed(() => !!this.currentAdmin()?.complexId);

  async login(email: string, password: string): Promise<void> {
    const tokens = await firstValueFrom(this.authService.login({ email, password }));
    this.authService.saveTokens(tokens);
    this.currentAdmin.set(this.decodeUser(tokens.accessToken));
    // The app initializer can't cover this: on a cold load at /login there was no token
    // to fetch flags with. Failure is non-fatal — guards retry.
    await this.loadFeatureFlags();
  }

  logout(): void {
    this.authService.clearTokens();
    this.currentAdmin.set(null);
    this.featureFlagStore.reset();
    this.readinessStore.reset();
    this.partnerTermsStore.reset();
    this.complexStore.reset();
    this.invoicingSettingsStore.reset();
    this.venueAccessStore.reset();
    // Stripe Account Sessions are cleared too, but from the other side: `ConnectEmbeddedService`
    // watches `currentAdmin` and ends its sessions when it changes. Injecting it here would pull
    // ngx-translate into every consumer of this store, for a service most sessions never build.
    this.router.navigate(['/login']);
  }

  /** Flags need an authenticated admin with a complex; anything else 401s or 400s. */
  async loadFeatureFlags(): Promise<void> {
    if (!this.isAuthenticated() || !this.hasComplex()) return;
    try {
      await this.featureFlagStore.load();
    } catch {
      // Startup and sign-in must survive a flag outage — features stay locked until a retry.
    }
  }

  setCurrentAdmin(accessToken: string): void {
    this.currentAdmin.set(this.decodeUser(accessToken));
  }

  async tryRestoreSession(): Promise<void> {
    const token = this.authService.getAccessToken();
    if (token && !this.authService.isTokenExpired()) {
      this.currentAdmin.set(this.decodeUser(token));
      return;
    }

    const refreshToken = this.authService.getRefreshToken();
    if (refreshToken) {
      try {
        const tokens = await firstValueFrom(this.authService.refresh(refreshToken));
        this.authService.saveTokens(tokens);
        this.currentAdmin.set(this.decodeUser(tokens.accessToken));
        return;
      } catch {
        this.authService.clearTokens();
      }
    }

    this.currentAdmin.set(null);
  }

  private decodeUser(token: string): AdminUser {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return {
      id: payload.sub ?? payload.nameid ?? '',
      email: payload.email ?? '',
      fullName: payload.name ?? payload.unique_name ?? '',
      role: payload.role ?? '',
      complexId: payload.complex_id || null
    };
  }
}
